import {observer} from "mobx-react";
import {useContext} from "react";
import {Button, Chip, Skeleton} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/Lock";
import {Frame} from "../components/common/Frame";
import {Header} from "../components/sections/Header";
import {AppRoutes} from "../AppRoutes";
import {useNavigate, useParams} from "react-router";
import {AppContext} from "../stores/AppStore";
import useInstance from "../hooks/useInstance";
import {LessonStore, LessonStoreContext} from "../stores/LessonStore";
import {LessonHeader} from "../components/lessons/LessonHeader";
import {LessonChapterContent} from "../components/lessons/LessonChapterContent";

export const LessonPage = observer(() => {
  const {id} = useParams<{id?: string}>();
  const app = useContext(AppContext)!;
  const navigate = useNavigate();
  const store = useInstance(() => new LessonStore(app, id ?? ""), [app, id]);

  if (!id || !store) return null;

  const lessonLabel = store?.lesson?.title ?? "...";

  const content = (
    <Frame direction="column" grow>
      <Header
        pathCrumbs={[
          [AppRoutes.index, "Home"],
          [AppRoutes.lessons, "Lessons"],
          [undefined, lessonLabel],
        ]}
        action={!app.auth.isLessonEditor ? undefined : store.lesson?.locked ? (
          <Chip icon={<LockIcon/>} label="Locked" variant="outlined"/>
        ) : (
          <Button
            variant="outlined"
            startIcon={<EditIcon/>}
            onClick={() => navigate(AppRoutes.lessonEditorPath(id))}
          >
            Edit
          </Button>
        )}
      />

      <Frame
        direction="column"
        gap={2}
        padding={2}
        contentWidth
        grow
      >
        {store?.loading ? (
          <LessonPageSkeleton />
        ) : store?.lesson ? (
          <>
            <LessonHeader />
            {/* Sequential chapter view - no sidebar */}
            <LessonChapterContent />
          </>
        ) : null}
      </Frame>
    </Frame>
  );

  return (
    <LessonStoreContext.Provider value={store}>
      {content}
    </LessonStoreContext.Provider>
  );
});

const LessonPageSkeleton = () => {
  return (
    <Frame direction="column" gap={2}>
      {/* Header skeleton */}
      <Frame appearance="card" padding={3} direction="column" gap={1.5} radius={1.5}>
        <Skeleton variant="text" width="60%" height={40} />
        <Frame row gap={2}>
          <Skeleton variant="rounded" width={100} height={24} />
          <Skeleton variant="rounded" width={120} height={24} />
          <Skeleton variant="rounded" width={100} height={24} />
        </Frame>
      </Frame>

      {/* Content skeleton - sequential chapters */}
      <Frame direction="column" gap={2}>
        <Frame appearance="card" padding={3} direction="column" gap={3} radius={1.5}>
          <Skeleton variant="text" width="50%" height={32} />
          <Skeleton variant="rectangular" width="100%" height={100} />
          <Skeleton variant="rectangular" width="100%" height={150} />
        </Frame>
        <Frame appearance="card" padding={3} direction="column" gap={3} radius={1.5}>
          <Skeleton variant="text" width="40%" height={32} />
          <Skeleton variant="rectangular" width="100%" height={100} />
        </Frame>
      </Frame>
    </Frame>
  );
};
